import {
  useSyncExternalStore,
  useCallback,
  useMemo,
  startTransition,
} from 'react';
import {
  getSelectedTenant,
  setSelectedTenant,
} from '@/shared/utils/tenantStorage';

// Event for tenant change
const TENANT_CHANGE_EVENT = 'tenantChanged';

// External store for tenant
const tenantStore = {
  getSnapshot() {
    const tenant = getSelectedTenant();
    return tenant || null;
  },

  getServerSnapshot() {
    return null;
  },

  subscribe(callback) {
    const handleChange = () => callback();
    window.addEventListener(TENANT_CHANGE_EVENT, handleChange);
    window.addEventListener('storage', handleChange);
    return () => {
      window.removeEventListener(TENANT_CHANGE_EVENT, handleChange);
      window.removeEventListener('storage', handleChange);
    };
  },
};

const useSelectedTenant = () => {
  const tenantId = useSyncExternalStore(
    tenantStore.subscribe,
    tenantStore.getSnapshot,
    tenantStore.getServerSnapshot,
  );

  const updateTenant = useCallback((newTenantId) => {
    const oldTenantId = getSelectedTenant();

    // Only update if value actually changed
    if (oldTenantId === newTenantId) {
      return;
    }

    // Use startTransition for non-urgent updates
    startTransition(() => {
      setSelectedTenant(newTenantId);

      // Invalidate all queries when tenant changes
      if (window.queryClient) {
        window.queryClient.invalidateQueries();
      }

      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent(TENANT_CHANGE_EVENT));
    });
  }, []);

  return useMemo(
    () => ({
      tenantId: tenantId || null,
      setTenantId: updateTenant,
      hasTenant: !!tenantId,
    }),
    [tenantId, updateTenant],
  );
};

export default useSelectedTenant;
